/**
 * context/app/rows.js - row shaping shared by AppContext and its callers:
 * a project as the lists hold it, camelCase <-> snake_case, and a quote's
 * local date and year. Moved out of AppContext.jsx, which re-exports the
 * public ones.
 */

// The opportunity columns GET /api/accounts reads (routes/accounts.js: a Won
// opportunity linked to a site makes that site a Customer). An edit that
// changes none of them cannot change an account, and the accounts list is the
// heaviest read there is - so most opportunity edits re-read the rows alone.
// customer too (10/11/26): a save links - or makes - the customer's account,
// and the accounts' Won / Open / Lost counts follow it
const ACCOUNT_COLUMNS = ['status', 'account_id', 'excluded_from_totals', 'customer'];
const num = (v) => {
  const n = parseFloat(v);
  return Number.isFinite(n) ? n : 0;
};

/**
 * A saved project row (the PUT / POST answer, snake_case) as the lists hold
 * it: camelCase like getList, plus the two figures GET /api/projects works out
 * in SQL (routes/projects.js: COGS honouring quoted_cogs_override, and the
 * margin, null until costed). The quiet re-read that follows replaces them
 * with the server's own.
 */
export function listedProject(raw) {
  const r = toCamel(raw);
  if (r && r.totalQuotedCogs === undefined) {
    const override = r.quotedCogsOverride;
    const cogs =
      override !== null && override !== undefined && override !== '' ? num(override) : num(r.quotedLaborCost) + num(r.quotedExpenses);
    const price = num(r.quotedPrice);
    r.totalQuotedCogs = cogs;
    r.quotedGrossMarginPct = price > 0 && cogs > 0 ? Math.round(((price - cogs) / price) * 10000) / 100 : null;
  }
  return r;
}

/** `rows` with `row` in it: replaced where its id is, added on top when new,
    taken out when it is excluded from totals (GET /api/projects leaves those out). */
export function withSavedRow(rows, row) {
  if (!row || row.id === null || row.id === undefined) return rows;
  const i = rows.findIndex((r) => String(r.id) === String(row.id));
  if (row.excludedFromTotals) return i < 0 ? rows : rows.filter((_, j) => j !== i);
  if (i < 0) return [row, ...rows];
  const next = rows.slice();
  next[i] = { ...rows[i], ...row };
  return next;
}

/** Can these project changes (camel or snake keys) have moved an account? */
const changesTouchAccounts = (changes) => Object.keys(toSnake(changes || {})).some((k) => ACCOUNT_COLUMNS.includes(k));

/* ── snake_case ↔ camelCase helpers ── */
export function toCamel(obj) {
  if (Array.isArray(obj)) return obj.map(toCamel);
  if (obj !== null && typeof obj === 'object') {
    return Object.fromEntries(Object.entries(obj).map(([k, v]) => [k.replace(/_([a-z])/g, (_, c) => c.toUpperCase()), toCamel(v)]));
  }
  return obj;
}

export function toSnake(obj) {
  if (Array.isArray(obj)) return obj.map(toSnake);
  if (obj !== null && typeof obj === 'object') {
    return Object.fromEntries(Object.entries(obj).map(([k, v]) => [k.replace(/[A-Z]/g, (c) => `_${c.toLowerCase()}`), toSnake(v)]));
  }
  return obj;
}

/* ── quote dates ──
   quote_date is stored as text ('YYYY-MM-DD'). new Date('2026-01-01') reads a
   bare day as UTC midnight, which is 31 December in US time zones, so a bid
   quoted on 1 January landed in the previous year. A bare day is read as that
   local day; a full timestamp (the created_at fallback) parses as before. */
const BARE_DAY = /^(\d{4})-(\d{2})-(\d{2})$/;

/** A quote date as a local Date: 'YYYY-MM-DD' is local midnight of that day. */
export function quoteDateLocal(v) {
  const m = typeof v === 'string' && v.trim().match(BARE_DAY);
  return m ? new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3])) : new Date(v);
}

/** The year a quote date belongs to: 'YYYY-MM-DD' gives its first four characters (NaN when unreadable). */
export function quoteYear(v) {
  if (typeof v === 'string' && BARE_DAY.test(v.trim())) return Number(v.trim().slice(0, 4));
  return new Date(v).getFullYear();
}

export { ACCOUNT_COLUMNS, changesTouchAccounts };
